import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/app/ui-bits";
import {
  Flag, ArrowRight, DotsSixVertical, CaretDown, CaretRight, ClockCounterClockwise, Warning, CheckCircle, ListPlus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import RaiseProcurementDialog from "@/components/app/RaiseProcurementDialog";

const PRIORITY_TONE = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-800",
  high: "bg-amber-100 text-amber-800",
  critical: "bg-red-100 text-red-800",
};

/**
 * ProjectDeliverablesTree
 * - Renders deliverables as a parent/child sub-breakdown tree
 * - HTML5 drag-and-drop reordering (within same parent group)
 * - Per-deliverable expandable audit timeline (lazy loaded)
 *
 * Props:
 *   project    — full project record (must include id, name, deliverables[])
 *   canEdit    — whether user can reorder / push-to-tasks
 *   onRowClick — open the RecordInspector with the deliverable
 *   onReload   — reload the parent projects list after a backend mutation
 */
export default function ProjectDeliverablesTree({ project, canEdit, onRowClick, onReload }) {
  const [order, setOrder] = useState(project.deliverables || []);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [audits, setAudits] = useState({});
  const [busyReorder, setBusyReorder] = useState(false);
  const [busyBatch, setBusyBatch] = useState(false);

  useEffect(() => { setOrder(project.deliverables || []); }, [project.deliverables]);

  // Phase 4 — batch task creation for confirmed deliverables
  const taskEligible = (order || []).filter((d) => d.confirmed && !d.pushed_task_id && d.owner);
  const unconfirmedCount = (order || []).filter((d) => !d.confirmed).length;
  const batchCreate = async () => {
    setBusyBatch(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/deliverables/create-tasks-batch`, {});
      const skippedNote = data.skipped_count ? ` · ${data.skipped_count} skipped` : "";
      toast.success(`${data.created_count} task(s) created${skippedNote}`);
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyBatch(false); }
  };

  // Build the tree: group by parent_deliverable_id (null = root)
  const tree = useMemo(() => {
    const byParent = new Map();
    (order || []).forEach((d) => {
      const k = d.parent_deliverable_id || "__root__";
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k).push(d);
    });
    for (const [, arr] of byParent) {
      arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }
    return byParent;
  }, [order]);

  const milestoneCount = (order || []).filter((d) => d.milestone).length;

  const toggleExpand = (id) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
    // Lazy-load audit on first open
    if (!audits[id]) loadAudit(id);
  };

  const loadAudit = async (deliverableId) => {
    try {
      const { data } = await api.get(`/audit-logs/trail/project_deliverable/${deliverableId}?limit=50`);
      setAudits((a) => ({ ...a, [deliverableId]: data.items || [] }));
    } catch (e) {
      setAudits((a) => ({ ...a, [deliverableId]: { error: formatApiError(e) } }));
    }
  };

  // Drag and drop handlers (reorder within same parent group only)
  const onDragStart = (e, d) => {
    setDragId(d.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", d.id);
  };
  const onDragOver = (e, d) => {
    e.preventDefault();
    if (dragId && d.id !== dragId) setOverId(d.id);
  };
  const onDrop = async (e, target) => {
    e.preventDefault();
    setOverId(null);
    if (!dragId || dragId === target.id) { setDragId(null); return; }
    const dragged = (order || []).find((x) => x.id === dragId);
    if (!dragged) { setDragId(null); return; }
    // Only allow reorder within same parent group
    if ((dragged.parent_deliverable_id || null) !== (target.parent_deliverable_id || null)) {
      toast.error("Drag within the same parent group only.");
      setDragId(null);
      return;
    }
    // Compute new order
    const siblings = (order || []).filter(
      (d) => (d.parent_deliverable_id || null) === (target.parent_deliverable_id || null),
    ).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const without = siblings.filter((d) => d.id !== dragId);
    const targetIdx = without.findIndex((d) => d.id === target.id);
    const newSiblings = [
      ...without.slice(0, targetIdx),
      dragged,
      ...without.slice(targetIdx),
    ];
    const newSiblingIds = newSiblings.map((d) => d.id);
    // Optimistic update
    setOrder((prev) => prev.map((d) => {
      const idx = newSiblingIds.indexOf(d.id);
      return idx === -1 ? d : { ...d, sort_order: (idx + 1) * 10 };
    }));
    setDragId(null);
    setBusyReorder(true);
    try {
      // Send full project order so backend reorders entries
      const fullOrder = (order || []).map((d) => d.id);
      // Replace the siblings slice with the new order
      const finalOrder = [];
      const used = new Set();
      for (const id of fullOrder) {
        if (used.has(id)) continue;
        const d = (order || []).find((x) => x.id === id);
        if (!d) continue;
        const isSibling = newSiblingIds.includes(id);
        if (!isSibling) {
          finalOrder.push(id);
          used.add(id);
          continue;
        }
        // Push all siblings in their new order at the first sibling slot
        if (!newSiblingIds.some((nid) => used.has(nid))) {
          for (const nid of newSiblingIds) { finalOrder.push(nid); used.add(nid); }
        }
      }
      await api.post(`/projects/${project.id}/deliverables/reorder`, { order: finalOrder });
      toast.success("Order updated");
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setOrder(project.deliverables || []);
    } finally {
      setBusyReorder(false);
    }
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };

  const renderRow = (d, depth = 0) => {
    const children = tree.get(d.id) || [];
    const isExpanded = expanded.has(d.id);
    const isOver = overId === d.id;
    const isDragging = dragId === d.id;
    const audit = audits[d.id];
    return (
      <React.Fragment key={d.id}>
        <li
          draggable={canEdit}
          onDragStart={(e) => onDragStart(e, d)}
          onDragOver={(e) => onDragOver(e, d)}
          onDrop={(e) => onDrop(e, d)}
          onDragEnd={onDragEnd}
          onClick={() => onRowClick?.(d)}
          data-testid={`deliverable-row-${d.id}`}
          className={`relative flex flex-wrap items-start gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-slate-50/70 transition ${
            d.milestone ? "border-amber-200 bg-amber-50/50" : "border-slate-100 bg-white"
          } ${isOver ? "ring-2 ring-blue-400" : ""} ${isDragging ? "opacity-50" : ""}`}
          style={{ marginLeft: depth * 18 }}
        >
          {canEdit && (
            <span className="cursor-grab pt-0.5 text-slate-400" title="Drag to reorder"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`deliv-drag-${d.id}`}>
              <DotsSixVertical size={14} weight="bold" />
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleExpand(d.id); }}
            className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title={isExpanded ? "Collapse details" : "Expand details (children + audit timeline)"}
            data-testid={`deliv-expand-${d.id}`}
          >
            {isExpanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
          </button>
          <span className="w-24 flex-shrink-0 font-mono-alt text-xs text-slate-500">
            {formatDate(d.due_date) || "—"}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 font-medium text-slate-900">
              {d.milestone && <Flag size={11} weight="fill" className="text-amber-600" />}
              <span>{d.title || <span className="text-slate-400">Untitled</span>}</span>
              {d.priority && d.priority !== "medium" && (
                <span className={`font-mono-alt text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${PRIORITY_TONE[d.priority] || "bg-slate-100 text-slate-700"}`}>
                  {d.priority}
                </span>
              )}
              {d.requires_approval && (
                <span className="font-mono-alt text-[10px] uppercase rounded bg-indigo-100 text-indigo-700 px-1.5 py-0.5">approval</span>
              )}
              {d.contract_clause && (
                <span className="font-mono-alt text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">cl. {d.contract_clause}</span>
              )}
              {d.pushed_task_id && (
                <span className="font-mono-alt text-[10px] rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700">→ Task</span>
              )}
              {d.confirmed ? (
                !d.pushed_task_id && (
                  <span className="font-mono-alt text-[10px] rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-emerald-700"
                        title={d.confirmed_by ? `Confirmed by ${d.confirmed_by}` : "Confirmed"}
                        data-testid={`deliv-confirmed-chip-${d.id}`}>confirmed</span>
                )
              ) : (
                <span className="font-mono-alt text-[10px] rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-700"
                      title="Formal confirmation required before a task can be created"
                      data-testid={`deliv-unconfirmed-chip-${d.id}`}>awaiting confirmation</span>
              )}
              {(d.depends_on || []).length > 0 && (
                <span className="font-mono-alt text-[10px] rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-sky-700"
                      title={`Depends on: ${(d.depends_on || []).map((id) => (order.find((x) => x.id === id)?.title) || id).join(", ")}`}
                      data-testid={`deliv-deps-chip-${d.id}`}>
                  ⇠ {(d.depends_on || []).length} dep
                </span>
              )}
              {d.responsible_type === "external" && (
                <span className="font-mono-alt text-[10px] rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-violet-700"
                      data-testid={`deliv-external-chip-${d.id}`}>
                  ext · {d.external_party || "external party"}
                </span>
              )}
              {children.length > 0 && (
                <span className="font-mono-alt text-[10px] rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">
                  {children.length} sub
                </span>
              )}
            </div>
            {d.description && (
              <div className="mt-0.5 text-xs leading-relaxed text-slate-600">{d.description}</div>
            )}
            {(d.owner || d.reviewer || d.approver) && (
              <div className="mt-1 flex flex-wrap gap-3 font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">
                {d.owner && <span>owner · {d.owner}</span>}
                {d.reviewer && <span>reviewer · {d.reviewer}</span>}
                {d.approver && <span>approver · {d.approver}</span>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {/* Inline progress chip — click cycles 0 → 25 → 50 → 75 → 100 → 0 */}
            {canEdit ? (
              <button
                type="button"
                onClick={async () => {
                  const cur = Number(d.completion_pct) || 0;
                  const next = cur >= 100 ? 0 : Math.min(100, Math.floor(cur / 25) * 25 + 25);
                  try {
                    await api.patch(`/projects/${project.id}/deliverables/${d.id}`, { completion_pct: next });
                    toast.success(`Progress · ${next}%`);
                    onReload?.();
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className={`inline-flex h-6 w-12 items-center justify-center rounded-md border font-mono-alt text-[10px] font-bold tabular-nums transition ${
                  (Number(d.completion_pct) || 0) >= 100
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : (Number(d.completion_pct) || 0) >= 50
                    ? "border-blue-300 bg-blue-50 text-blue-800"
                    : (Number(d.completion_pct) || 0) > 0
                    ? "border-amber-300 bg-amber-50 text-amber-800"
                    : "border-slate-200 bg-slate-50 text-slate-500"
                } hover:bg-white hover:shadow-sm`}
                title="Click to bump progress (0→25→50→75→100→0)"
                data-testid={`deliv-progress-chip-${d.id}`}
              >
                {Number(d.completion_pct) || 0}%
              </button>
            ) : (
              <span className="inline-flex h-6 w-12 items-center justify-center rounded-md border border-slate-200 bg-slate-50 font-mono-alt text-[10px] font-bold tabular-nums text-slate-600">
                {Number(d.completion_pct) || 0}%
              </span>
            )}
            <StatusBadge status={d.status} />
            {canEdit && !d.confirmed && (
              <Button
                size="sm" variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/projects/${project.id}/deliverables/${d.id}/confirm`);
                    toast.success(data.task_id ? "Confirmed · task created" : "Deliverable confirmed");
                    onReload?.();
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                data-testid={`confirm-deliv-${d.id}`}
                title="Formally confirm this deliverable — required before its task can be created"
              >
                <CheckCircle size={11} className="mr-1" /> Confirm
              </Button>
            )}
            {canEdit && d.confirmed && !d.pushed_task_id && (
              <Button
                size="sm" variant="outline"
                onClick={async () => {
                  try {
                    await api.post(`/projects/${project.id}/deliverables/${d.id}/push-to-tasks`);
                    toast.success("Pushed to Tasks");
                    onReload?.();
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                data-testid={`push-task-${d.id}`}
                title="Push this deliverable to Tasks module"
              >
                <ArrowRight size={11} className="mr-1" /> Push
              </Button>
            )}
            {!d.procurement_request_id && (
              <RaiseProcurementDialog
                project={project}
                deliverable={d}
                onRaised={onReload}
                trigger={
                  <Button size="sm" variant="outline"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          data-testid={`raise-proc-${d.id}`}
                          title="Raise a procurement request from this deliverable">
                    ⌖ Proc
                  </Button>
                }
              />
            )}
          </div>
        </li>

        {/* Expanded inline: audit timeline */}
        {isExpanded && (
          <li className="ml-9 mt-1 mb-2 rounded-md border border-slate-200 bg-slate-50/40 p-3" data-testid={`deliv-audit-${d.id}`}>
            <div className="overline mb-1.5 flex items-center gap-1 text-slate-500">
              <ClockCounterClockwise size={11} /> Audit timeline
            </div>
            {!audit && <div className="text-[11px] text-slate-500">Loading audit…</div>}
            {audit && audit.error && (
              <div className="flex items-center gap-1 text-[11px] text-amber-700">
                <Warning size={11} weight="fill" /> {audit.error}
              </div>
            )}
            {Array.isArray(audit) && audit.length === 0 && (
              <div className="text-[11px] text-slate-500">No audit entries for this deliverable yet.</div>
            )}
            {Array.isArray(audit) && audit.length > 0 && (
              <ol className="space-y-1.5">
                {audit.map((a) => (
                  <li key={a.id} className="rounded border border-slate-200 bg-white px-2 py-1.5 text-[11px]">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-700">
                        {a.action}
                      </span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-700">{a.user_email || a.user_id || "system"}</span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-500 tabular-nums">{formatDate(a.created_at)}</span>
                    </div>
                    {a.comments && (
                      <div className="mt-0.5 text-slate-700 italic">&ldquo;{a.comments}&rdquo;</div>
                    )}
                    {Array.isArray(a.diff) && a.diff.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {a.diff.slice(0, 6).map((c, idx) => (
                          <li key={idx} className="font-mono-alt text-[10px]">
                            <span className="text-slate-600">{c.field}:</span>{" "}
                            <span className="line-through text-rose-600">{String(c.from ?? "—")}</span>
                            <span className="mx-1 text-slate-400">→</span>
                            <span className="text-emerald-700">{String(c.to ?? "—")}</span>
                          </li>
                        ))}
                        {a.diff.length > 6 && (
                          <li className="text-[10px] text-slate-400">+{a.diff.length - 6} more change(s)</li>
                        )}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </li>
        )}

        {/* Children */}
        {children.length > 0 && children.map((c) => renderRow(c, depth + 1))}
      </React.Fragment>
    );
  };

  const roots = tree.get("__root__") || [];

  return (
    <section className="border-t border-slate-100 px-5 py-4" data-testid={`deliverables-tree-${project.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="overline text-slate-500">
          Deliverables · sub-breakdown
          {busyReorder && <span className="ml-2 text-[10px] text-blue-600">saving order…</span>}
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono-alt uppercase tracking-wider text-slate-500">
          <span>{(order || []).length} total</span>
          {unconfirmedCount > 0 && <span className="text-amber-700">{unconfirmedCount} unconfirmed</span>}
          {milestoneCount > 0 && <span className="text-amber-700">{milestoneCount} milestone(s)</span>}
          {canEdit && taskEligible.length > 0 && (
            <Button
              size="sm" variant="outline"
              className="h-6 border-emerald-300 px-2 text-[10px] text-emerald-700 hover:bg-emerald-50"
              disabled={busyBatch}
              onClick={batchCreate}
              data-testid={`batch-create-tasks-${project.id}`}
              title="Create Tasks for every confirmed deliverable that doesn't have one yet"
            >
              <ListPlus size={11} className="mr-1" />
              {busyBatch ? "Creating…" : `Create ${taskEligible.length} task${taskEligible.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
      </div>
      {roots.length === 0 ? (
        <div className="text-sm text-slate-500">No deliverables defined yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {roots.map((d) => renderRow(d, 0))}
        </ul>
      )}
      {canEdit && roots.length > 1 && (
        <p className="mt-2 text-[10px] text-slate-400">
          Drag the grip handle to reorder within a parent group.
        </p>
      )}
    </section>
  );
}
